import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  fetchAuditLogs,
  exportAuditLogsCSV,
  exportAuditLogsJSON,
} from "@services/auditLogs";
import {
  getDefaultStartDate,
  getDefaultEndDate,
} from "@utils/auditFormatters";

import ToastHost from "@components/clinic/ToastHost";
import AuditFiltersBar from "@components/audit/AuditFiltersBar";
import AuditLogsTable from "@components/audit/AuditLogsTable";

export default function AuditLogsPage() {
  const isOwner = useMemo(() => {
    const v = localStorage.getItem("is_owner");
    return v === "true" || v === true;
  }, []);

  const [state, setState] = useState({
    logs: [],
    loading: false,
    error: null,
  });

  const [filters, setFilters] = useState({
    startDate: getDefaultStartDate(),
    endDate: getDefaultEndDate(),
    actionType: "ALL",
    resourceType: "ALL",
  });

  const [toast, setToast] = useState(null);
  const [exporting, setExporting] = useState(null);

  const navigate = useNavigate();

  const handleBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate("/");
  };

  const loadAuditLogs = async () => {
    setState((p) => ({ ...p, loading: true, error: null }));
    try {
      const res = await fetchAuditLogs({
        start_date: filters.startDate,
        end_date: filters.endDate,
        action: filters.actionType,
        resource_type: filters.resourceType,
      });

      // Backend returns { data: { logs: [...], pagination: {...} } }
      const logs = res.data?.logs || res.data || [];

      setState({ logs, loading: false, error: null });
    } catch (e) {
      setState((p) => ({
        ...p,
        loading: false,
        error: e?.message || "Failed to load audit logs",
      }));
      setToast({
        kind: "error",
        title: "Failed to load audit logs",
        message: e?.message || "Request failed",
      });
    }
  };

  useEffect(() => {
    if (isOwner) loadAuditLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOwner]);

  const handleApplyFilters = () => {
    loadAuditLogs();
  };

  const handleExportCSV = async () => {
    setExporting("csv");
    try {
      const blob = await exportAuditLogsCSV({
        start_date: filters.startDate,
        end_date: filters.endDate,
        action: filters.actionType,
        resource_type: filters.resourceType,
      });

      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `audit-logs-${filters.startDate}-to-${filters.endDate}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      setToast({
        kind: "success",
        title: "Export successful",
        message: "Audit logs exported as CSV.",
      });
    } catch (e) {
      setToast({
        kind: "error",
        title: "Export failed",
        message: e?.message || "Failed to export CSV",
      });
    } finally {
      setExporting(null);
    }
  };

  const handleExportJSON = async () => {
    setExporting("json");
    try {
      const blob = await exportAuditLogsJSON({
        start_date: filters.startDate,
        end_date: filters.endDate,
        action: filters.actionType,
        resource_type: filters.resourceType,
      });

      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `audit-logs-${filters.startDate}-to-${filters.endDate}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      setToast({
        kind: "success",
        title: "Export successful",
        message: "Audit logs exported as JSON.",
      });
    } catch (e) {
      setToast({
        kind: "error",
        title: "Export failed",
        message: e?.message || "Failed to export JSON",
      });
    } finally {
      setExporting(null);
    }
  };

  if (!isOwner) {
    return (
      <div className="p-6">
        <div className="p-5 border rounded-2xl bg-white">
          <div className="font-semibold text-slate-900">Access denied</div>
          <div className="text-sm text-slate-600">
            Only the clinic owner can view audit logs.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 flex flex-col min-h-0 h-full gap-4">
      <div className="flex items-center">
        <button
          type="button"
          onClick={handleBack}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border bg-white hover:bg-slate-50 shadow-sm text-sm font-medium"
        >
          ← Back
        </button>
      </div>

      <ToastHost toast={toast} onClose={() => setToast(null)} />

      {/* Hero Header */}
      <div className="relative overflow-hidden rounded-2xl border bg-white">
        <div className="absolute inset-0 bg-gradient-to-r from-sky-500/12 via-purple-500/12 to-emerald-500/12" />
        <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-sky-500/10 blur-2xl" />
        <div className="absolute -bottom-24 -left-24 w-64 h-64 rounded-full bg-purple-500/10 blur-2xl" />

        <div className="relative p-5 flex items-start justify-between gap-4">
          <div>
            <div className="inline-flex items-center gap-2 text-xs font-medium px-2.5 py-1 rounded-full bg-white/70 border">
              <svg
                className="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={1.8}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 2l1.5 5.5L19 9l-5.5 1.5L12 16l-1.5-5.5L5 9l5.5-1.5L12 2z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M19 14l.9 3.1L23 18l-3.1.9L19 22l-.9-3.1L15 18l3.1-.9L19 14z"
                />
              </svg>
              Admin • Activity tracking
            </div>

            <h1 className="mt-2 text-2xl font-semibold text-slate-900">
              Audit Logs
            </h1>

            <p className="text-sm text-slate-600 mt-1">
              View and export user activity logs for compliance and security
              monitoring.
            </p>
          </div>

          {state.loading && (
            <div className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border bg-white/70">
              <span className="w-2 h-2 rounded-full bg-sky-500 animate-pulse" />
              <span className="text-sm text-slate-700">Loading…</span>
            </div>
          )}
        </div>
      </div>

      <AuditFiltersBar
        startDate={filters.startDate}
        endDate={filters.endDate}
        actionType={filters.actionType}
        resourceType={filters.resourceType}
        onStartDateChange={(value) =>
          setFilters((p) => ({ ...p, startDate: value }))
        }
        onEndDateChange={(value) =>
          setFilters((p) => ({ ...p, endDate: value }))
        }
        onActionTypeChange={(value) =>
          setFilters((p) => ({ ...p, actionType: value }))
        }
        onResourceTypeChange={(value) =>
          setFilters((p) => ({ ...p, resourceType: value }))
        }
        onApply={handleApplyFilters}
        loading={state.loading}
      />

      <AuditLogsTable
        logs={state.logs}
        total={state.logs.length}
        loading={state.loading}
        error={state.error}
        onExportCSV={handleExportCSV}
        onExportJSON={handleExportJSON}
        exporting={exporting}
      />
    </div>
  );
}
